export type ResizeObserverDimensions = {
    width: number
    height: number
}

const throttle = (f, delay) => {
    let timer: any = 0
    return function(...args) {
        clearTimeout(timer)
        timer = setTimeout(() => f.apply(this, args), delay)
    }
}

// Singleton state
let resizeObserverInstance: ResizeObserver | null = null
const elementCallbacks = new Map<Element, Set<() => void>>()

const createResizeObserver = (): ResizeObserver => {
    return new ResizeObserver(
        throttle(entries => {
            // Execute callbacks only for the elements that actually resized
            entries.forEach(entry => {
                const element = entry.target as Element
                const callbacks = elementCallbacks.get(element)
                if (callbacks) {
                    callbacks.forEach(callback => callback())
                }
            })
        }, 200),
    )
}

const getResizeObserver = (): ResizeObserver => {
    if (!resizeObserverInstance) {
        resizeObserverInstance = createResizeObserver()
    }
    return resizeObserverInstance
}

const addResizeCallback = (element: Element, callback: () => void): void => {
    if (!elementCallbacks.has(element)) {
        elementCallbacks.set(element, new Set())
    }
    elementCallbacks.get(element)!.add(callback)
}

// Stop observing a specific element and remove its callbacks
export const unobserveElement = (element: Element): void => {
    const observer = getResizeObserver()
    observer.unobserve(element)
    elementCallbacks.delete(element)
}

// Convenient function that combines getting observer + adding callback + observing element
export const observeElement = (element: Element, callback: () => void): ResizeObserver => {
    addResizeCallback(element, callback)
    const observer = getResizeObserver()
    observer.observe(element)
    return observer
}
